import { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { appStore } from "@store/appStore";
import { useSnapshot } from "valtio";
import { Feature } from "@models/feature.model";
import { PermissionService } from "@services/permissions/permissionService";
import type { Feature as ApiFeature } from "@services/permissions/commands/cmdCreateFeature";

interface Props {
    open: boolean;
    onClose: () => void;
}

export default function ImpersonationPermissionsModal({ open, onClose }: Props) {
    const [allFeatures, setAllFeatures] = useState<ApiFeature[]>([]);
    const { loggedInUser } = useSnapshot(appStore);

    useEffect(() => {
        if (!open) return;
        const permService = new PermissionService();
        permService.listFeatures().then(setAllFeatures).catch(console.error);
    }, [open]);

    const currentFeatureNames = new Set(
        (loggedInUser?.permissions?.features ?? []).map((f) => f.name),
    );

    const getDescendants = (featureName: string): ApiFeature[] => {
        const byName = new Map(allFeatures.map((f) => [f.name, f]));
        const root = byName.get(featureName);
        if (!root) return [];
        const result: ApiFeature[] = [];
        const stack = childrenOf(root.id);
        while (stack.length) {
            const f = stack.pop()!;
            result.push(f);
            stack.push(...childrenOf(f.id));
        }
        return result;
    };

    const toggleFeature = (featureName: string) => {
        const perms = appStore.loggedInUser?.permissions;
        if (!perms) return;

        if (currentFeatureNames.has(featureName)) {
            const descendants = getDescendants(featureName);
            const toRemove = new Set([featureName, ...descendants.map((d) => d.name)]);
            perms.features = perms.features.filter((f) => !toRemove.has(f.name));
        } else {
            const descendants = getDescendants(featureName);
            const toAdd: Feature[] = [featureName, ...descendants.map((d) => d.name)]
                .filter((name) => !currentFeatureNames.has(name))
                .map((name) => {
                    const feat = new Feature();
                    feat.name = name;
                    feat.level = 0b1111; // full access
                    feat.path = buildPath(name);
                    return feat;
                });
            perms.features = [...perms.features, ...toAdd];
        }
    };

    const buildPath = (featureName: string): string[] => {
        const path: string[] = [];
        const byId = new Map(allFeatures.map((f) => [f.id, f]));
        const byName = new Map(allFeatures.map((f) => [f.name, f]));
        let current = byName.get(featureName);
        while (current) {
            path.unshift(current.name);
            current = current.parent_id ? byId.get(current.parent_id) : undefined;
        }
        return path;
    };

    const roots = allFeatures.filter((f) => !f.parent_id);
    const childrenOf = (parentId: number) => allFeatures.filter((f) => f.parent_id === parentId);

    const renderFeature = (feature: ApiFeature, depth: number) => {
        const children = childrenOf(feature.id);
        return (
            <div key={feature.id}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={currentFeatureNames.has(feature.name)}
                            onChange={() => toggleFeature(feature.name)}
                            size="small"
                        />
                    }
                    label={feature.name}
                    sx={{ display: "flex", mt: 0.5, ml: depth * 3 }}
                />
                {children.map((child) => renderFeature(child, depth + 1))}
            </div>
        );
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    background: "#191c40",
                    color: "#fff",
                    py: 1.5,
                    px: 2.5,
                }}
            >
                Impersonation Features
                <IconButton onClick={onClose} size="small" sx={{ color: "#fff" }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: 2, pb: 3 }}>
                {roots.map((root) => renderFeature(root, 0))}
            </DialogContent>
        </Dialog>
    );
}
